import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Calculator } from "lucide-react";

const SERVICES = [
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "deep", l: "Deep Cleaning" },
  { v: "move_in", l: "Move-in" },
  { v: "move_out", l: "Move-out" },
  { v: "recurring", l: "Recurring" },
];
const ADDONS = ["windows", "fridge", "oven", "laundry", "carpet", "garage", "balcony"];

export default function NewBooking() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState({
    customer_id: "", service_type: "residential", date: "", time: "09:00",
    address: "", notes: "", price: 0, square_footage: 1000, rooms: 3,
    addons: [], is_recurring: false, status: "scheduled", assigned_employee_id: "",
  });

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  async function calc() {
    const { data } = await api.post("/estimate", {
      service_type: form.service_type, square_footage: Number(form.square_footage),
      rooms: Number(form.rooms), addons: form.addons, is_recurring: form.is_recurring,
    });
    setEstimate(data);
    setForm((f) => ({ ...f, price: data.total }));
  }

  function toggleAddon(a) {
    setForm((f) => ({
      ...f, addons: f.addons.includes(a) ? f.addons.filter((x) => x !== a) : [...f.addons, a],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    const payload = { ...form, assigned_employee_id: form.assigned_employee_id || null,
      square_footage: Number(form.square_footage), rooms: Number(form.rooms), price: Number(form.price) };
    const { data } = await api.post("/jobs", payload);
    nav(`/jobs/${data.id}`);
  }

  return (
    <div className="p-8 lg:p-10" data-testid="new-booking-page">
      <h1 className="font-display text-4xl font-semibold tracking-tight mb-1">New Booking</h1>
      <p className="text-sm text-slate-500 mb-8">Create a job and generate an estimate.</p>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Customer</label>
            <select required data-testid="booking-customer"
              value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Service Type</label>
              <select data-testid="booking-service"
                value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
                {SERVICES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Assign Cleaner</label>
              <select data-testid="booking-employee"
                value={form.assigned_employee_id} onChange={(e) => setForm({ ...form, assigned_employee_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
                <option value="">Unassigned</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Date</label>
              <input required type="date" data-testid="booking-date"
                value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Time</label>
              <input type="time" data-testid="booking-time"
                value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</label>
              <select data-testid="booking-status"
                value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
                <option value="new_lead">New Lead</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Address</label>
            <input data-testid="booking-address"
              value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Square footage</label>
              <input type="number" data-testid="booking-sqft"
                value={form.square_footage} onChange={(e) => setForm({ ...form, square_footage: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Rooms</label>
              <input type="number" data-testid="booking-rooms"
                value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Add-ons</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ADDONS.map((a) => (
                <button type="button" key={a} onClick={() => toggleAddon(a)} data-testid={`addon-${a}`}
                  className={`px-3 py-1 rounded-md border text-xs font-medium transition-colors ${
                    form.addons.includes(a) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                  }`}>
                  {a}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="booking-recurring"
              checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}/>
            Recurring service (10% discount)
          </label>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Notes</label>
            <textarea data-testid="booking-notes" rows={3}
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 h-fit lg:sticky lg:top-8">
          <div className="font-display font-medium mb-4">Estimate</div>
          <button type="button" onClick={calc} data-testid="calc-estimate-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 rounded-md mb-4 transition-colors">
            <Calculator className="w-4 h-4"/> Calculate
          </button>
          {estimate && (
            <div className="text-sm space-y-1 text-slate-600 mb-4" data-testid="estimate-result">
              <div className="flex justify-between"><span>Base</span><span>${estimate.base.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Square footage</span><span>${estimate.sqft_cost.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Rooms</span><span>${estimate.rooms_cost.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Add-ons</span><span>${estimate.addon_cost.toFixed(2)}</span></div>
              {estimate.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Recurring discount</span><span>−${estimate.discount.toFixed(2)}</span></div>}
              <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-semibold text-slate-900">
                <span>Total</span><span>${estimate.total.toFixed(2)}</span>
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Final price</label>
            <input type="number" step="0.01" data-testid="booking-price"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
          </div>
          <button type="submit" data-testid="create-booking-btn"
            className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium py-2.5 rounded-md transition-colors">
            Create Booking
          </button>
        </div>
      </form>
    </div>
  );
}
